<section class="alert-response-page">

    @if (pageState === 'loading') {
        <div class="alert-response-shell status-card">
            <app-progress-spinner></app-progress-spinner>
            <p>{{ 'Loading...' | translate }}</p>
        </div>
    }

    @if (pageState === 'error') {
        <div class="alert-response-shell status-card">
            <h2>{{ loadErrorTitle }}</h2>
            <p>{{ loadErrorMessage }}</p>
            @if (canRetryLoad) {
                <button type="button" class="ack-confirm" (click)="retryLoad()">
                    {{ 'Try again' | translate }}
                </button>
            }
        </div>
    }

    @if (pageState === 'revoked') {
        <div class="alert-response-shell status-card">
            <h2>{{ 'This link has been revoked.' | translate }}</h2>
            <p>{{ 'Nothing was recorded. Tell your supervisor the alert reached the wrong handset.' | translate }}</p>
        </div>
    }

    @if (pageState === 'ready' && data; as page) {
        <div class="alert-response-shell">

            <div class="ack-identity">
                <div class="k">{{ 'You are acknowledging as' | translate }}</div>
                <div class="who">{{ page.recipientName || ('This link recipient' | translate) }}</div>
                <div class="meta">
                    @if (page.recipientMaskedPhone) {
                        <span class="link-sent">
                            {{ 'Link sent by SMS to' | translate }} {{ page.recipientMaskedPhone }}
                        </span>
                    }
                    @if (page.tokenExpiresAt) {
                        <span class="link-sent">
                            &middot; {{ 'expires' | translate }} {{ page.tokenExpiresAt | dateFormat: timeFormat }}
                        </span>
                    }
                    @if (!isConfirmingRevoke) {
                        &middot;
                        <button type="button" class="not-you" (click)="startRevoke()">
                            {{ 'Not you?' | translate }}
                        </button>
                    }
                </div>

                @if (isConfirmingRevoke) {
                    <div class="revoke-confirm">
                        <p>{{ 'This revokes the link so nobody can use it. Nothing is recorded.' | translate }}</p>
                        <div class="revoke-actions">
                            <button type="button" class="revoke-yes" [disabled]="revokePending" (click)="confirmRevoke()">
                                {{ 'Revoke this link' | translate }}
                            </button>
                            <button type="button" class="revoke-no" [disabled]="revokePending" (click)="cancelRevoke()">
                                {{ 'Cancel' | translate }}
                            </button>
                        </div>
                    </div>
                }
            </div>

            <header class="ack-head" [ngClass]="headerSeverityClass">
                <span class="severity-chip compact" [ngClass]="headerSeverityClass">{{ headerSeverityLabel | translate }}</span>
                <div class="rule">{{ headerTitle }}</div>
                <div class="meta">
                    @if (page.siteName) {
                        <span class="meta">{{ page.siteName }}</span>
                    }
                    @if (page.detectedAt) {
                        <span class="meta">
                            &middot; {{ 'detected' | translate }} <b>{{ page.detectedAt | dateFormat: timeFormat }}</b>
                        </span>
                    }
                    <br />
                    {{ breachSummaryLabel }}
                </div>
            </header>

            @if (isAcknowledged) {
                <div class="acked-banner">
                    <mat-icon>check</mat-icon>
                    <div>
                        <b>{{ acknowledgementLabel }}</b>
                        <br />
                        {{ 'One tap. Every alert in this notification moved to Acknowledged - listed below so it is not a surprise.' | translate }}
                    </div>
                </div>
            }

            @if (transitionRows.length) {
                <div class="transition-list">
                    @for (transition of transitionRows; track trackById($index, transition)) {
                        <div class="transitioned">
                            <mat-icon>check</mat-icon>
                            <b>{{ transition.nodeName }}</b> &middot; {{ transition.fromLabel }} &rarr; {{ transition.toLabel }}
                        </div>
                    }
                </div>
            }
            @if (!isAcknowledged) {
                <section class="ack-sec">
                    <div class="ack-sec-title">{{ 'Where this is' | translate }}</div>

                    @if (isMapUsable) {
                        <div class="ack-map-bar">
                            <span class="who">{{ page.siteName || ('Alerting nodes' | translate) }}</span>
                            <span class="n">&middot; {{ locateSummaryLabel }}</span>
                            <button type="button" class="ack-map-recentre" (click)="recentreMap()">
                                <mat-icon>my_location</mat-icon>
                                {{ 'Recentre' | translate }}
                            </button>
                        </div>
                    }

                    <div class="map-anchor" #locateAnchor></div>

                    @if (mapState === 'failed') {
                        <div class="ack-map-down">
                            <mat-icon>warning</mat-icon>
                            <div>
                                <b>{{ "The map didn't load." | translate }}</b>
                                {{ 'No connection to the basemap.' | translate }}
                                {{ 'The alert, the readings and the acknowledgement below are all here and all work - they came with the page.' | translate }}
                                <button type="button" class="retry" (click)="retryMap()">
                                    <mat-icon>refresh</mat-icon>
                                    {{ 'Try the map again' | translate }}
                                </button>
                            </div>
                        </div>
                    }

                    @if (!hasMappableNodes) {
                        <div class="ack-map-down">
                            <mat-icon>warning</mat-icon>
                            <div>
                                <b>{{ 'No recorded locations for these nodes.' | translate }}</b>
                                {{ 'Coverage is selected from the node list.' | translate }}
                            </div>
                        </div>
                    }

                    @if (isMapUsable) {
                        <div class="ack-map-foot">
                            <span class="key"><i></i> {{ 'alerting' | translate }}</span>
                            <span class="key"><i class="plain"></i> {{ 'nearby' | translate }}</span>
                            <br />
                            {{ 'Nothing here is tappable yet. Coverage is recorded after you confirm, on the same map.' | translate }}
                        </div>
                    }
                </section>
            }

            @if (readingRows.length) {
                <section class="ack-sec">
                    <div class="ack-sec-title">
                        {{ 'What happened' | translate }} <span class="n">{{ readingRows.length }}</span>
                        {{ 'nodes' | translate }}
                    </div>

                    @for (reading of readingRows; track trackById($index, reading)) {
                        <article
                            class="reading-row"
                            [class.nodata]="!reading.isReporting">
                            <span class="rn">
                                <span class="severity-chip compact" [ngClass]="reading.severityClass">
                                    {{ reading.severityLabel | translate }}
                                </span>
                                {{ reading.nodeName }}
                            </span>
                            <span class="rv" [ngClass]="reading.bandTextClass">{{ reading.readingValue | nullDisplay }}</span>
                            <span class="ru">{{ reading.unitLabel }}</span>
                            @if (reading.startedAt) {
                                <span class="rt">{{ reading.startedAt | dateFormat: timeFormat }}</span>
                            }
                            @if (reading.thresholdValue != null) {
                                <span class="rlim">
                                    {{ 'Rule threshold' | translate }} <b>{{ reading.thresholdValue }}</b> {{ reading.unitLabel }}
                                    @if (reading.detailLabel) {
                                        &middot; {{ reading.detailLabel }}
                                    }
                                </span>
                            }
                            @if (!reading.isReporting) {
                                <span class="rnd">
                                    <mat-icon>signal_cellular_off</mat-icon>
                                    <span>
                                        {{ 'No data since' | translate }}
                                        <b>{{ reading.lastReportedAt | dateFormat: timeFormat }}</b>.
                                        {{ 'The alert is still open - a node that stopped reporting has not come back under the threshold.' | translate }}
                                    </span>
                                </span>
                            }
                        </article>
                    }
                </section>
            }

            @if (!isAcknowledged && page.canAcknowledge) {
                <section class="ack-sec">
                    <button type="button" class="ack-confirm" [disabled]="acknowledgePending" (click)="acknowledge()">
                        <mat-icon>check</mat-icon>
                        {{ acknowledgePending ? ('Recording...' | translate) : ('I have seen this' | translate) }}
                    </button>
                    <div class="ack-note">
                        {{ 'Covers all' | translate }} <strong>{{ readingRows.length }}</strong>
                        {{ 'nodes listed above.' | translate }}
                        <br />
                        {{ 'Records you, and the time. Nothing else on this page is required.' | translate }}
                    </div>
                </section>
            }

            @if (isAcknowledged && page.canCaptureResponse) {
                <section class="ack-sec">
                    <div class="ack-sec-title">{{ 'What did you do?' | translate }}</div>

                    <div class="ack-scope-note">
                        <mat-icon>info</mat-icon>
                        <span>
                            {{ 'The suggestion is at the top because the rule named it.' | translate }}
                            <strong>{{ 'You are not limited to it' | translate }}</strong>
                            {{ '- pick whatever you actually did.' | translate }}
                        </span>
                    </div>

                    <div class="ctrl-search">
                        <mat-icon>search</mat-icon>
                        <input
                            type="text"
                            [placeholder]="'Search controls...' | translate"
                            [ngModel]="controlSearchText"
                            (ngModelChange)="onControlSearchChanged($event)" />
                    </div>

                    @for (group of controlGroups; track trackById($index, group)) {
                        <div class="ctrl-group">
                            {{ group.title }}
                            @if (group.hint) {
                                <span class="why">{{ group.hint }}</span>
                            }
                        </div>
                        @for (control of group.options; track trackById($index, control)) {
                            <button
                                type="button"
                                class="ctrl-opt"
                                [class.suggested]="control.isSuggested"
                                [class.on]="selectedControlIds[control.id]"
                                [disabled]="responseSubmitted"
                                (click)="toggleControl(control.id)">
                                <span class="box"></span>
                                <span class="lbl">
                                    {{ control.name }}
                                    @if (control.categoryName || control.lastUsedAt) {
                                        <span class="sub">
                                            @if (control.categoryName) {
                                                {{ control.categoryName }}
                                            }
                                            @if (control.lastUsedAt) {
                                                &middot; {{ 'last used' | translate }}
                                                {{ control.lastUsedAt | dateFormat: dateTimeFormat }}
                                                @if (control.lastUsedByName) {
                                                    {{ 'by' | translate }} {{ control.lastUsedByName }}
                                                }
                                            }
                                        </span>
                                    }
                                </span>
                                @if (control.isSuggested) {
                                    <span class="rank-tag">{{ 'From rule' | translate }}</span>
                                }
                            </button>
                        }
                    }

                    @if (hiddenControlCount > 0 || showAllControls) {
                        <button
                            type="button"
                            class="ctrl-opt ctrl-more"
                            (click)="toggleShowAllControls()">
                            <mat-icon>{{ showAllControls ? 'expand_less' : 'expand_more' }}</mat-icon>
                            @if (!showAllControls) {
                                <span class="lbl">
                                    {{ 'Show all' | translate }} {{ controlCount }}
                                </span>
                            }
                            @if (showAllControls) {
                                <span class="lbl">{{ 'Show fewer' | translate }}</span>
                            }
                        </button>
                    }

                    @if (!hasControlMatches) {
                        <div class="ack-map-down">
                            <mat-icon>warning</mat-icon>
                            <div>{{ 'No controls match that search.' | translate }}</div>
                        </div>
                    }
                </section>

                <section class="ack-sec">
                    <div class="ack-sec-title">{{ 'When did it start?' | translate }}</div>
                    <div class="started-row">
                        <span class="node-chip locked">{{ startedAt | dateFormat: timeFormat }}</span>
                        @if (!isEditingStartedAt) {
                            <button type="button" class="node-chip" (click)="startEditingStartedAt()">
                                <mat-icon>schedule</mat-icon>
                                {{ 'Change' | translate }}
                            </button>
                        }
                        @if (isEditingStartedAt) {
                            <input
                                class="form-input"
                                [owlDateTimeTrigger]="startedAtPicker"
                                [owlDateTime]="startedAtPicker"
                                [ngModel]="startedAt"
                                (ngModelChange)="onStartedAtChanged($event)"
                                [max]="maxStartedAt"
                                readonly
                                [placeholder]="'When did it start?' | translate" />
                            <owl-date-time #startedAtPicker (afterPickerClosed)="confirmStartedAt()"></owl-date-time>
                        }
                    </div>
                    <div class="form-help">
                        {{ 'The time you say it started, not the time you typed it.' | translate }}
                    </div>
                </section>

                <section class="ack-sec">
                    <div class="ack-sec-title">{{ 'Which nodes does this cover?' | translate }}</div>

                    <div class="seg">
                        <button
                            type="button"
                            [class.on]="coverageSurface === 'map'"
                            [class.dead]="!isMapUsable"
                            (click)="selectCoverageSurface('map')">
                            <mat-icon>map</mat-icon>
                            {{ 'Map' | translate }}
                            @if (!isMapUsable) {
                                <span class="why">{{ 'off' | translate }}</span>
                            }
                        </button>
                        <button type="button" [class.on]="coverageSurface === 'list'" (click)="selectCoverageSurface('list')">
                            <mat-icon>format_list_bulleted</mat-icon>
                            {{ 'List' | translate }}
                        </button>
                    </div>

                    @if (coverageSurface === 'map') {
                        <div class="ack-map-bar">
                            <span class="who">{{ 'Tap a node to add or remove it' | translate }}</span>
                            <button type="button" class="ack-map-recentre" (click)="recentreMap()">
                                <mat-icon>my_location</mat-icon>
                                {{ 'Recentre' | translate }}
                            </button>
                        </div>
                        <div class="map-anchor" #coverageAnchor></div>
                    }

                    @if (!isMapUsable) {
                        <div class="ack-map-down">
                            <mat-icon>warning</mat-icon>
                            <div>
                                <b>{{ 'No map, so you are picking from the list.' | translate }}</b>
                                {{ 'Same nodes, same selection, nearest first.' | translate }}
                                @if (hasMappableNodes) {
                                    <button type="button" class="retry" (click)="retryMap()">
                                        <mat-icon>refresh</mat-icon>
                                        {{ 'Try the map again' | translate }}
                                    </button>
                                }
                            </div>
                        </div>
                    }

                    @if (showCoverageList) {
                        @if (lockedNodeRows.length) {
                            <div class="node-group">
                                <div class="node-group-title">{{ 'The nodes that alerted' | translate }}</div>
                                @for (node of lockedNodeRows; track trackById($index, node)) {
                                    <span class="node-chip locked">
                                        <mat-icon>check</mat-icon> {{ node.name }}
                                    </span>
                                }
                            </div>
                        }

                        @if (nearbyNodeRows.length) {
                            <div class="node-group">
                                <div class="node-group-title">{{ 'Nearby - tap to add' | translate }}</div>
                                @for (node of nearbyNodeRows; track trackById($index, node)) {
                                    <button
                                        type="button"
                                        class="node-chip"
                                        [class.on]="node.isCovered"
                                        [disabled]="responseSubmitted"
                                        (click)="toggleNodeCoverage(node.id)">
                                        @if (node.isCovered) {
                                            <mat-icon>check</mat-icon>
                                        }
                                        @if (!node.isCovered) {
                                            <mat-icon>add</mat-icon>
                                        }
                                        {{ node.name }}
                                        <span class="dist">{{ node.distanceLabel }}</span>
                                    </button>
                                }
                            </div>
                        }

                        @if (unlocatedNodeRows.length) {
                            <div class="node-group">
                                <div class="node-group-title">{{ 'No recorded location' | translate }}</div>
                                @for (node of unlocatedNodeRows; track trackById($index, node)) {
                                    <button
                                        type="button"
                                        class="node-chip"
                                        [class.on]="node.isCovered"
                                        [disabled]="responseSubmitted"
                                        (click)="toggleNodeCoverage(node.id)">
                                        @if (node.isCovered) {
                                            <mat-icon>check</mat-icon>
                                        }
                                        @if (!node.isCovered) {
                                            <mat-icon>add</mat-icon>
                                        }
                                        {{ node.name }}
                                        <span class="dist">{{ node.distanceLabel }}</span>
                                    </button>
                                }
                            </div>
                        }
                    }

                    <div class="cover-out">
                        <div class="k">
                            {{ 'Covering' | translate }} <b>{{ coveredNodeRows.length }}</b> {{ 'nodes' | translate }}
                        </div>
                        @for (node of coveredNodeRows; track trackById($index, node)) {
                            @if (node.isLocked) {
                                <span class="node-chip locked">
                                    <mat-icon>check</mat-icon> {{ node.name }}
                                </span>
                            }
                            @if (!node.isLocked) {
                                <button
                                    type="button"
                                    class="node-chip on"
                                    [disabled]="responseSubmitted"
                                    (click)="toggleNodeCoverage(node.id)">
                                    <mat-icon>close</mat-icon> {{ node.name }}
                                </button>
                            }
                        }
                    </div>

                    <div class="ack-map-foot">
                        <span class="key"><i class="sel"></i> {{ 'covered' | translate }}</span>
                        <span class="key"><i class="plain"></i> {{ 'not covered' | translate }}</span>
                        <br />
                        {{ 'The alerting nodes are covered by default and cannot be removed here. There is no radius field and no area to draw.' | translate }}
                    </div>
                </section>

                <section class="ack-sec">
                    <div class="ack-sec-title">
                        {{ 'Anything to add?' | translate }}
                        <span class="soft">&mdash; {{ 'optional' | translate }}</span>
                    </div>
                    <textarea
                        rows="3"
                        class="form-textarea"
                        [placeholder]="'Free text, if the controls above do not say it' | translate"
                        [disabled]="responseSubmitted"
                        [ngModel]="responseNote"
                        (ngModelChange)="onResponseNoteChanged($event)"></textarea>
                </section>

                <section class="ack-sec">
                    <button
                        type="button"
                        class="ack-confirm"
                        [class.done]="responseSubmitted"
                        [disabled]="responsePending || disableSubmitResponse"
                        (click)="submitResponse()">
                        <mat-icon>check</mat-icon>
                        {{ responseSubmitted ? ('Response recorded' | translate) : ('Save what I did' | translate) }}
                    </button>
                    <div class="ack-note">
                        {{ selectedControlCount }} {{ 'controls' | translate }} &middot;
                        {{ coveredNodeRows.length }} {{ 'nodes' | translate }} &middot;
                        {{ 'from' | translate }} {{ startedAt | dateFormat: timeFormat }}
                        <br />
                        {{ 'You can add more later from the same link.' | translate }}
                    </div>
                    <button type="button" class="ack-back" (click)="goToPreviousStep()">
                        <mat-icon>arrow_back</mat-icon>
                        {{ 'Back' | translate }}
                    </button>
                </section>
            }

            <div class="map-parking">
                <div
                    class="ack-map-frame"
                    #mapFrame
                    [class.readonly]="!isAcknowledged"
                    [class.loading]="mapState === 'loading'"
                    [class.failed]="mapState === 'failed'">
                    <div class="map-canvas"></div>
                </div>
            </div>
        </div>
    }
</section>
